import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import profile from '../profile.json';
import { Profile } from '../types/profile';

const typedProfile = profile as Profile;

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const } }
};

export default function Projects(): React.ReactElement {
    return (
        <section id='projects' className="py-24 lg:py-32">
            <div className="container-main">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={fadeUp}
                    className="mb-16"
                >
                    <div className="section-label">
                        <span>Projects</span>
                    </div>
                    <h2 className="section-title">Things I've built</h2>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6">
                    {typedProfile['projects'].map((project, idx) => (
                        <motion.article
                            key={project.title}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: idx * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className={`card-spotlight p-8 flex flex-col group ${project.demoUrl ? 'cursor-pointer' : ''}`}
                            onClick={() => project.demoUrl && window.open(project.demoUrl, '_blank')}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <motion.div
                                    whileHover={{ rotate: -5, scale: 1.05 }}
                                    className="w-14 h-14 rounded-2xl bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faFolder} className="text-primary-500 text-2xl" />
                                </motion.div>
                                <div className="flex gap-4 relative z-20">
                                    <a 
                                        href={project.link} 
                                        target='_blank' 
                                        rel='noreferrer'
                                        className="text-slate-500 dark:text-dark-400 hover:text-primary-500 transition-colors text-xl cursor-pointer"
                                        aria-label="GitHub"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <FontAwesomeIcon icon={faGithub} />
                                    </a>
                                    <a 
                                        href={project.demoUrl || project.link} 
                                        target='_blank' 
                                        rel='noreferrer'
                                        className="text-slate-500 dark:text-dark-400 hover:text-primary-500 transition-colors text-lg cursor-pointer"
                                        aria-label="External link"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                    </a>
                                </div>
                            </div>

                            <h3 className="text-xl font-semibold text-slate-900 dark:text-white group-hover:text-primary-500 transition-colors mb-4">
                                {project.title}
                            </h3>
                            
                            <p className="text-slate-700 dark:text-dark-300 leading-relaxed flex-grow mb-6">
                                {project.content}
                            </p>

                            <div className="flex flex-wrap gap-2 mt-auto">
                                {project.skills.map((skill) => (
                                    <span key={skill} className="badge">{skill}</span>
                                ))}
                            </div>
                        </motion.article>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="text-center mt-16"
                >
                    <a 
                        href="https://github.com/gogouravr" 
                        target="_blank"
                        rel="noreferrer"
                        className="btn-secondary inline-flex"
                    >
                        <FontAwesomeIcon icon={faGithub} />
                        <span>View more on GitHub</span>
                    </a>
                </motion.div>
            </div>
        </section>
    );
}
